import {type Static, Type} from '@sinclair/typebox'
import {TypeCompiler} from '@sinclair/typebox/compiler'
import type {Big as BigJs} from 'big.js'

import {parseRetailer, type Product, rawProductSchema} from '@/models/productModel'
import ParseError from '@/utils/errors/ParseError'
import TransformError from '@/utils/errors/TransformError'
import parseNumber from '@/utils/numbers/parseNumber'

export enum CheckoutResult {
  Success = 'success',
  Failure = 'failure',
}

const rawCartSchema = Type.Object({
  moreInfo: Type.Object({
    deliveryFee: Type.Optional(Type.String({default: '0'})),
    estimatedDelivery: Type.Optional(Type.String({default: ''})),
    wallet: Type.Optional(
      Type.Object({
        availableBalance: Type.String(),
        frozenBalance: Type.String(),
      }),
    ),
  }),
  lineItems: Type.Optional(Type.Array(Type.Unknown(), {default: []})), // Validate later
})
export type RawCart = Static<typeof rawCartSchema>
const rawCartTypeCheck = TypeCompiler.Compile(rawCartSchema)

export const rawCartItemSchema = Type.Object({
  retailer: rawProductSchema.properties.retailer,
  seller: rawProductSchema.properties.seller,
  itemId: rawProductSchema.properties.id,
  title: rawProductSchema.properties.title,
  imageUrl: rawProductSchema.properties.image,
  price: rawProductSchema.properties.price,
  currency: Type.String(),
  url: rawProductSchema.properties.url,
  retailer_url: rawProductSchema.properties.retailer_url,
  quantity: Type.Number(),
  variation: Type.Optional(Type.Record(Type.String(), Type.String())),
  estimatedDeliveryDate: Type.Optional(Type.Union([Type.String(), Type.Null()])),
})

export type RawCartItem = Static<typeof rawCartItemSchema>
const rawCartItemTypeCheck = TypeCompiler.Compile(rawCartItemSchema)

export interface CartItem
  extends Omit<RawCartItem, 'currency' | 'itemId' | 'imageUrl' | 'price' | 'retailer_url'> {
  id: string
  image: string
  price: number
}

export interface Cart {
  delivery: {
    fee: number
  }
  wallet: {
    availableBalance: number
    frozenBalance: number
  }
  items: CartItem[]
}

export interface CartWithGroupedItems {
  delivery: Cart['delivery']
  wallet: Cart['wallet']
  items: Record<string, CartItem[]>
  itemsLength: number
  itemsValue: BigJs
  originalItems: CartItem[]
}

// TODO: create default using typebox
export const createDefaultCart = (): Cart => ({
  delivery: {
    fee: 0,
  },
  wallet: {
    availableBalance: 0,
    frozenBalance: 0,
  },
  items: [],
})

//------------------------------------------------------------------------------

export function transformCartItem(rawCartItem: RawCartItem): CartItem {
  let parsedProductPrice: number
  let parsedRetailer: Product['retailer']
  let parsedSeller: Product['seller']

  // Product is considered invalid if the following information is not valid
  try {
    parsedProductPrice = parseNumber(String(rawCartItem.price))
  } catch (e) {
    throw new TransformError('ProductPrice', 'Product price is not a number', e, [
      {
        field: 'price',
        value: rawCartItem.price,
      },
    ])
  }

  try {
    parsedRetailer = parseRetailer(rawCartItem.retailer)
  } catch (e) {
    throw new TransformError('Retailer', 'Invalid retailer', e, [
      {
        field: 'retailer',
        value: rawCartItem.retailer,
      },
    ])
  }

  if (rawCartItem.seller) {
    try {
      parsedSeller = parseRetailer(rawCartItem.seller)
    } catch (e) {
      throw new TransformError('Seller', 'Invalid seller', e, [
        {
          field: 'seller',
          value: rawCartItem.seller,
        },
      ])
    }
  }

  return {
    url: rawCartItem.url ?? rawCartItem.retailer_url,
    retailer: parsedRetailer,
    seller: parsedSeller,
    price: parsedProductPrice,
    id: rawCartItem.itemId,
    image: rawCartItem.imageUrl,
    title: rawCartItem.title,
    quantity: rawCartItem.quantity,
    variation: rawCartItem.variation,
    estimatedDeliveryDate: rawCartItem.estimatedDeliveryDate,
  }
}

export function parseCartItem(rawCartItem: unknown): CartItem {
  if (!rawCartItemTypeCheck.Check(rawCartItem)) {
    throw new ParseError(
      'CartItem',
      'Invalid cart item',
      Array.from(rawCartItemTypeCheck.Errors(rawCartItem)),
    )
  }

  return transformCartItem(rawCartItem)
}

export function transformCartItems(rawCartItems: RawCartItem[], retentive = true): CartItem[] {
  return rawCartItems
    .map(rawCartItem => {
      try {
        return transformCartItem(rawCartItem)
      } catch (e) {
        if (!retentive) throw e
        return false
      }
    })
    .filter(Boolean)
}

export function parseCartItems(rawCartItems: unknown[], retentive = true): CartItem[] {
  return rawCartItems
    .map(rawCartItem => {
      try {
        return parseCartItem(rawCartItem)
      } catch (e) {
        if (!retentive) throw e
        return false
      }
    })
    .filter(Boolean)
}

export function transformCart(rawCart: RawCart, retentive = true): Cart {
  return {
    delivery: {
      fee: Number(rawCart.moreInfo.deliveryFee) || 0,
    },
    wallet: {
      availableBalance: Number(rawCart.moreInfo.wallet?.availableBalance) || 0,
      frozenBalance: Number(rawCart.moreInfo.wallet?.frozenBalance) || 0,
    },
    // This is because we always try to retain as much as information as possible, so we delay the validation
    items: rawCart.lineItems ? parseCartItems(rawCart.lineItems, retentive) : [],
  }
}

export function parseCart(rawCart: unknown, retentive = true): Cart {
  if (!rawCartTypeCheck.Check(rawCart)) {
    throw new ParseError('Cart', 'Invalid cart', Array.from(rawCartTypeCheck.Errors(rawCart)))
  }

  return transformCart(rawCart, retentive)
}

//------------------------------------------------------------------------------

export function transformRawCartItem(cartItem: CartItem, currency: string): RawCartItem {
  return {
    retailer: cartItem.retailer,
    seller: cartItem.seller,
    itemId: cartItem.id,
    title: cartItem.title,
    imageUrl: cartItem.image,
    price: cartItem.price,
    currency,
    url: cartItem.url,
    quantity: cartItem.quantity,
    variation: cartItem.variation,
  }
}
